import { Branch } from '../../../api/dealer-api-interface-franchisee';
import { customElement, property } from 'lit/decorators.js';
import { emptyGuid } from '../../../../webmodule-common/other/api/guid';
import { isEmptyOrSpace } from '../../../../webmodule-common/other/ui/string-helper-functions';
import { LitElementBase } from '../../../../webmodule-common/other/litelement-base';
import { TenantLoginPublicInfo } from '../../../api/dealer-api-interface-user';

import { FormInputSelectValue } from '../../../../webmodule-common/other/ui/templateresult/form-input-types';
import { getToolTip } from '../../../../webmodule-common/other/tooltip';
import { html, TemplateResult } from 'lit';
import { LangDashboardFilter } from '../../../../localization/scripts/dealer-franchisee/dashboard/widget/dashboard-filter-lang';
import {
  WebmoduleChangeEvent,
  WebmoduleSelect
} from '../../../../webmodule-common/components/src/webmodule-components';

export interface DateRange {
  id: string;
  label: string;
  startDate: Date;
  endDate: Date;
}

@customElement('wm-dashboard-filter')
export class DashboardFilter extends LitElementBase {
  @property({ attribute: false }) public dates?: DateRange[];
  @property({ attribute: false }) public branches?: Branch[];
  @property({ attribute: false }) public users?: TenantLoginPublicInfo[];

  @property({ attribute: false }) public selectedDate?: DateRange;
  @property({ attribute: false }) public selectedBranchId?: string;
  @property({ attribute: false }) public selectedUserId?: string;

  // Shown beside Filter Data when set. The overview tab shares this filter and measures its range
  // across more than one date, so it leaves this unset and no icon is shown.
  @property({ attribute: false }) public filterTooltip?: string;

  protected dateChangeEvent(e: WebmoduleChangeEvent) {
    const target = e.target as WebmoduleSelect | null;

    if (!target) return;

    const dateRangeId = target.value;
    const dateRange = this.dates?.find(dr => dr.id == dateRangeId);
    this.dispatchCustom('date-changed', {
      date: dateRange
    });
  }

  protected branchChangeEvent(e: WebmoduleChangeEvent) {
    const target = e.target as WebmoduleSelect | null;

    if (!target) return;

    const branchId = target.value;
    this.dispatchCustom('branch-changed', {
      branchId: branchId
    });
  }

  protected userChangeEvent(e: WebmoduleChangeEvent) {
    const target = e.target as WebmoduleSelect | null;

    if (!target) return;

    const userId = target.value;
    this.dispatchCustom('user-changed', {
      userId: userId
    });
  }

  protected sendResetFilterEvent(_e: Event) {
    this.dispatchCustom('reset-filters', {});
  }

  protected render(): TemplateResult {
    const dateRangeTemplate = () =>
      this.dates?.map(d => {
        const option: FormInputSelectValue = {
          value: d.id,
          text: d.label,
          disabled: false
        };
        return option;
      });

    const branchTemplate = () =>
      this.branches?.map(b => {
        const option: FormInputSelectValue = {
          value: b.id,
          text: b.name,
          disabled: false
        };
        return option;
      });
    const allBranchesOption: FormInputSelectValue | undefined =
      this.branches && this.branches.length > 1
        ? { disabled: false, value: emptyGuid, text: LangDashboardFilter.allBranches() }
        : undefined;

    const userTemplate = (): FormInputSelectValue[] => {
      return [
        { disabled: false, value: emptyGuid, text: LangDashboardFilter.allEmployees() },
        ...(this.users?.map(u => {
          const option: FormInputSelectValue = {
            value: u.id,
            text: u.friendlyName ?? u.userName ?? '',
            disabled: false
          };

          return option;
        }) ?? [])
      ];
    };
    const isUserDisabled = isEmptyOrSpace(this.selectedBranchId);
    const branches = branchTemplate();
    return html`
      <div class="page-dashboard-filter">
        <label>
          ${LangDashboardFilter.filterData()}
          ${this.filterTooltip ? html`<span class="filter-data-help">${getToolTip(this.filterTooltip)}</span>` : html``}
        </label>
        <webmodule-select
          @webmodule-change=${this.dateChangeEvent}
          id="selectedDateId"
          .value=${this.selectedDate?.id ?? ''}
          .values=${dateRangeTemplate() ?? []}
          placeholder=${LangDashboardFilter.selectDateRange()}
          size="small"
        >
        </webmodule-select>

        <webmodule-select
          @webmodule-change=${this.branchChangeEvent}
          id="selectedBranchId"
          .value=${(branches?.length ?? 0) > 0 ? (this.selectedBranchId ?? emptyGuid) : ''}
          .values=${allBranchesOption ? [allBranchesOption, ...(branches ?? [])] : (branches ?? [])}
          size="small"
          placeholder=${LangDashboardFilter.selectBranch()}
        >
        </webmodule-select>

        <webmodule-select
          @webmodule-change=${this.userChangeEvent}
          id="selectedUserId"
          ?disabled=${isUserDisabled}
          .value=${this.selectedUserId ?? emptyGuid}
          .values=${userTemplate() ?? []}
          size="small"
          placeholder=${LangDashboardFilter.selectUser()}
        >
        </webmodule-select>

        <webmodule-icon class="text-white" library="fa" name="fas-arrows-rotate" @click=${this.sendResetFilterEvent}>
        </webmodule-icon>
      </div>
    `;
  }
}
